/** Cursor-following placement with edge flipping (pure, unit-tested). */

export interface Placement {
  x: number;
  y: number;
  /** Placed to the left of the cursor because the right edge was too close. */
  flipX: boolean;
  /** Placed above the cursor because the bottom edge was too close. */
  flipY: boolean;
}

export interface PlaceOptions {
  /** Gap between cursor and tooltip. */
  offset?: number;
  /** Minimum distance from the viewport edges. */
  margin?: number;
}

/**
 * Default: below-right of the cursor. Flip to the left / above when the tooltip would cross the
 * right / bottom edge, then clamp so it never leaves the viewport (even when larger than it).
 */
export function placeTooltip(
  cursor: { x: number; y: number },
  size: { w: number; h: number },
  viewport: { w: number; h: number },
  opts: PlaceOptions = {},
): Placement {
  const off = opts.offset ?? 16;
  const m = opts.margin ?? 8;
  let x = cursor.x + off;
  let flipX = false;
  if (x + size.w > viewport.w - m) {
    x = cursor.x - off - size.w;
    flipX = true;
  }
  let y = cursor.y + off;
  let flipY = false;
  if (y + size.h > viewport.h - m) {
    y = cursor.y - off - size.h;
    flipY = true;
  }
  x = clamp(x, m, Math.max(m, viewport.w - m - size.w));
  y = clamp(y, m, Math.max(m, viewport.h - m - size.h));
  return { x, y, flipX, flipY };
}

const clamp = (v: number, lo: number, hi: number): number => Math.min(hi, Math.max(lo, v));
